// src/pages/task/views/AnchoredPopover.tsx
// Small portal popover anchored to a rect, used by the calendar for the
// "+N more" day panel and for inline quick-create. It lives in a portal
// because the calendar grid clips its own overflow, so an in-cell panel would
// be cut off on the last row and the right-hand columns.

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface AnchoredPopoverProps {
  /** Viewport rect of the element the panel belongs to. */
  anchor: DOMRect | null;
  onClose: () => void;
  /** Accessible name of the panel. */
  label: string;
  width?: number;
  children: ReactNode;
}

const MARGIN = 8;

export function AnchoredPopover({ anchor, onClose, label, width = 240, children }: AnchoredPopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Place the panel below the anchor, flipping above and clamping to the
  // viewport when there is not enough room.
  useLayoutEffect(() => {
    if (!anchor) {
      setPosition(null);
      return;
    }
    const height = panelRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - anchor.bottom;
    const top = spaceBelow < height + MARGIN && anchor.top > height + MARGIN
      ? Math.max(MARGIN, anchor.top - height - MARGIN)
      : Math.min(anchor.bottom + MARGIN, Math.max(MARGIN, window.innerHeight - height - MARGIN));
    const left = Math.min(
      Math.max(MARGIN, anchor.left),
      Math.max(MARGIN, window.innerWidth - width - MARGIN),
    );
    // Compare by value: `children` changes identity on every parent render.
    setPosition((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, [anchor, width, children]);

  useEffect(() => {
    if (!anchor) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [anchor, onClose]);

  if (!anchor) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onMouseDown={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        role="dialog"
        aria-label={label}
        style={{
          top: position?.top ?? anchor.bottom + MARGIN,
          left: position?.left ?? anchor.left,
          width,
          visibility: position ? 'visible' : 'hidden',
        }}
        className="fixed z-50 rounded-lg border border-border bg-background p-2 shadow-xl"
      >
        {children}
      </div>
    </>,
    document.body,
  );
}
